/* Horizontal scroller
   - Per-element API: call `wrapper.HorizontalScroller(options)` to create or get an instance.
   - Backwards-compatible export: `initHorizontalScroller(wrapper, options)` forwards to the same implementation.
   - Features: hidden native scroller for momentum, visible replica scrollbar, draggable thumb, wheel/keyboard support,
     ResizeObserver + MutationObserver + image load handlers, and an `update()` method.
*/

export function initHorizontalScroller(wrapper, options = {}) {
    return createInstance(wrapper, options);
}

/* createInstance(wrapper, options)
   - Initialize a horizontal scroller instance for `wrapper`.
   - Sets up DOM elements (native scroller + replica/thumb) if missing, accessibility attributes, observers, and event handlers.
   - Returns an `instance` object with `update` and `destroy` methods.
*/
function createInstance(wrapper, options = {}) {
    const sliderSelector = (options.selectors && options.selectors.slider) || ".hs-slider",
        slider = wrapper.querySelector(sliderSelector);
    if (!slider) return null;

    // Accessibility: aria-label / description via data attributes or options
    const ariaLabel = options.ariaLabel || wrapper.getAttribute("data-hs-aria-label") || wrapper.getAttribute("aria-label");
    ariaLabel && wrapper.setAttribute("aria-label", ariaLabel);

    const description = options.ariaDescription || wrapper.getAttribute("data-hs-description");
    if (description) {
        let descId = wrapper.getAttribute("aria-describedby");
        if (!descId) {
            descId = "hs-desc-" + Math.random().toString(36).slice(2, 9);
            const descEl = document.createElement("div");
            (descEl.id = descId),
                (descEl.textContent = description),
                // visually hidden but available to screen readers
                (descEl.style.position = "absolute"),
                (descEl.style.left = "-9999px"),
                (descEl.style.width = "1px"),
                (descEl.style.height = "1px"),
                (descEl.style.overflow = "hidden"),
                wrapper.appendChild(descEl),
                wrapper.setAttribute("aria-describedby", descId);
        }
    }

    // Roles for assistive tech
    wrapper.hasAttribute("role") || wrapper.setAttribute("role", "region"),
        wrapper.hasAttribute("aria-roledescription") || wrapper.setAttribute("aria-roledescription", "carousel"),
        slider.hasAttribute("role") || slider.setAttribute("role", "list"),

        // Mark children as listitems for screen readers and add hs-item class
        Array.prototype.forEach.call(slider.children, function (ch, idx) {
            ch.classList.contains("hs-item") || ch.classList.add("hs-item"),
                ch.hasAttribute("role") || ch.setAttribute("role", "listitem"),
                ch.setAttribute("aria-posinset", idx + 1),
                ch.setAttribute("aria-setsize", slider.children.length);
        }),
        // Optim hint
        (slider.style.willChange = "transform");

    // Create (or find) the hidden native scroller element used to provide real scrolling behavior
   let nativeScroller = wrapper.querySelector(".hs-native-scroll");
    if (!nativeScroller) {
        (nativeScroller = document.createElement("div")),
            (nativeScroller.className = "hs-native-scroll"),
            nativeScroller.setAttribute("aria-hidden", "true"),
            (nativeScroller.tabIndex = 0); // focusable for keyboard scroll
        const inner = document.createElement("div");
        (inner.className = "hs-native-inner"), nativeScroller.appendChild(inner), wrapper.appendChild(nativeScroller);
    }

    // Create (or find) the visible replica scrollbar and thumb
    let replica = wrapper.querySelector(".hs-replica");
    if (!replica) {
        (replica = document.createElement("div")), (replica.className = "hs-replica");
        const thumb = document.createElement("div");
        (thumb.className = "hs-replica-thumb"), replica.appendChild(thumb), wrapper.appendChild(replica);
    }

    // Query important elements; allow for missing elements gracefully
    var replicaThumb = replica ? replica.querySelector(".hs-replica-thumb") : null,
        nativeInner = nativeScroller ? nativeScroller.querySelector(".hs-native-inner") : null;

    // measurement state
    let trackWidth = 0,
        viewPortWidth = 0,
        maxScroll = 0,
        // helpers to temporarily disable/restore CSS transitions during dragging
        prevTransition = null;
    function disableSliderTransition() {
        try {
            (prevTransition = slider.style.transition || ""), (slider.style.transition = "none");
        } catch {}
    }
    function restoreSliderTransition() {
        try {
            (slider.style.transition = prevTransition || ""), (prevTransition = null);
        } catch {}
    }

    // Debounced update scheduling using requestAnimationFrame
    let scheduled = null;
    function scheduleUpdate() {
        scheduled ||
            (scheduled = requestAnimationFrame(function () {
                (scheduled = null), updateSizes();
            }));
    }

    /* updateSizes()
     - Primary measurement function. Recalculates trackWidth, viewportWidth and maxScroll.
     - Updates nativeInner width (to provide a scrollable area), sizes the replica thumb,
       hides/shows the replica/native scroller when no overflow exists, and clamps scroll.
     - Call `instance.update()` to force immediate recomputation.
  */
    function updateSizes() {
        // compute robust track width from children positions
        if (
            ((trackWidth = 0),
            Array.prototype.forEach.call(slider.children, function (ch) {
                try {
                    trackWidth = Math.max(trackWidth, ch.offsetLeft + ch.offsetWidth);
                } catch {}
            }),
            (!trackWidth || trackWidth < slider.scrollWidth) && (trackWidth = slider.scrollWidth),
            (viewPortWidth = wrapper.clientWidth),
            (maxScroll = Math.max(0, trackWidth - viewPortWidth)),
            (slider.children ? slider.children.length : 0) === 0 || maxScroll <= 0)
        )
            try {
                replica.classList.add("hs-hidden"),
                    nativeScroller.classList.add("hs-hidden"),
                    replica.setAttribute("aria-hidden", "true"),
                    nativeScroller.setAttribute("aria-hidden", "true"),
                    replicaThumb && (replicaThumb.style.transform = "translateX(0px)"),
                    (slider.style.transform = "translateX(0px)"),
                    nativeScroller && (nativeScroller.scrollLeft = 0);
            } catch {}
        else
            try {
                replica.classList.remove("hs-hidden"),
                    nativeScroller.classList.remove("hs-hidden"),
                    replica.removeAttribute("aria-hidden"),
                    nativeScroller.removeAttribute("aria-hidden");
            } catch {}

        // set native inner width to the full content width so native scroll has correct range
        nativeInner && (nativeInner.style.width = trackWidth + "px");

        // compute replica thumb width proportional to visible area
        const track = (replica && replica.clientWidth) || viewPortWidth || 1;
        let thumbWidth = (viewPortWidth / Math.max(trackWidth, 1)) * track;
        (thumbWidth = Math.max(20, Math.min(track - 4, thumbWidth))), replicaThumb && (replicaThumb.style.width = Math.max(6, Math.round(thumbWidth)) + "px");

        // position the thumb to match native scrollLeft
        const thumbMax = Math.max(0, track - ((replicaThumb && replicaThumb.clientWidth) || 1)),
            ratio = maxScroll ? (nativeScroller ? nativeScroller.scrollLeft : 0) / maxScroll : 0,
            left = Math.round(ratio * thumbMax);
        replicaThumb && (replicaThumb.style.transform = "translateX(" + left + "px)"),
            nativeScroller && (nativeScroller.scrollLeft = Math.max(0, Math.min(nativeScroller.scrollLeft || 0, maxScroll))),
            syncFromNative();
    }

    /* syncFromNative()
     - Synchronize visible slider transform and replica thumb based on nativeScroller.scrollLeft.
     - This keeps the visual content and the replica in sync after programmatic scroll changes.
  */
    function syncFromNative() {
        try {
            const ns = (nativeScroller && nativeScroller.scrollLeft) || 0,
                left = -Math.round(ns);
            slider.style.transform = "translateX(" + left + "px)";
            const track = (replica && replica.clientWidth) || viewPortWidth || 1,
                thumbW = (replicaThumb && replicaThumb.clientWidth) || 1,
                thumbMax = Math.max(0, track - thumbW),
                ratio = maxScroll ? ns / maxScroll : 0,
                thumbLeft = Math.round(ratio * thumbMax);
            replicaThumb && (replicaThumb.style.transform = "translateX(" + thumbLeft + "px)");
        } catch {}
    }

    // --- Replica thumb dragging handlers ---
    let isThumbDragging = !1,
        thumbStartLeft = 0,
        thumbPointerStart = 0;

    /* onReplicaThumbPointerDown(e)
     - Start dragging the replica thumb. Captures pointer and disables transitions.
  */
    function onReplicaThumbPointerDown(e) {
        e.preventDefault(), (isThumbDragging = !0);
        try {
            replicaThumb && replicaThumb.setPointerCapture && replicaThumb.setPointerCapture(e.pointerId);
        } catch {}
        wrapper.classList.add("no-select"),
            disableSliderTransition(),
            (thumbStartLeft = (replicaThumb && replicaThumb.style && replicaThumb.style.transform && parseInt(replicaThumb.style.transform.replace(/[^0-9-]/g, ""), 10)) || 0),
            (thumbPointerStart = e.clientX);
    }

    /* onWindowPointerMoveForThumb(e)
     - Moves the native scroller while dragging the replica thumb.
  */
    function onWindowPointerMoveForThumb(t) {

        if (!isThumbDragging) return;
        const dx = t.clientX - thumbPointerStart,
            track = (replica && replica.clientWidth) || 1,
            thumbW = (replicaThumb && replicaThumb.clientWidth) || 1,
            left = Math.max(0, Math.min(track - thumbW, thumbStartLeft + dx)),
            ratio = track - thumbW ? left / (track - thumbW) : 0;
        nativeScroller && (nativeScroller.scrollLeft = Math.round(ratio * maxScroll)), syncFromNative();
    }

    /* onWindowPointerUpForThumb(e)
     - Release thumb drag state and restore transitions.
  */
    function onWindowPointerUpForThumb(t) {

        if (isThumbDragging) {
            isThumbDragging = !1;
            try {
                replicaThumb && replicaThumb.releasePointerCapture && replicaThumb.releasePointerCapture(t.pointerId);
            } catch {}
            wrapper.classList.remove("no-select"), restoreSliderTransition();
        }
    }

    // Attach thumb listeners only if the thumb exists
    replicaThumb && replicaThumb.addEventListener("pointerdown", onReplicaThumbPointerDown),
        window.addEventListener("pointermove", onWindowPointerMoveForThumb),
        window.addEventListener("pointerup", onWindowPointerUpForThumb),

        /* replica click handler
        - Clicking on the replica track jumps the native scroller to that position.
    */
        replica.addEventListener("click", function (e) {
            if (e.target === replicaThumb) return;
            const rect = replica.getBoundingClientRect(),
                clickX = e.clientX - rect.left,
                track = replica.clientWidth || 1,
                thumbW = (replicaThumb && replicaThumb.clientWidth) || 1,
                left = Math.max(0, Math.min(track - thumbW, clickX - thumbW / 2)),
                ratio = track - thumbW ? left / (track - thumbW) : 0;
            nativeScroller && (nativeScroller.scrollLeft = Math.round(ratio * maxScroll)), syncFromNative();
        });

    // --- Slider drag (drag the visible slider directly) ---
    let isDragging = !1,
        startX = 0,
        startScroll = 0;

    /* Slider pointerdown
     - Begins dragging the visible slider surface; updates nativeScroller to apply momentum.
  */
    slider.addEventListener("pointerdown", function (e) {
        if (e.target.NodeName === "A" || e.target.closest("a")) return;
        scheduleUpdate(), (isDragging = !0), (startX = e.clientX), (startScroll = nativeScroller ? nativeScroller.scrollLeft : 0);
        try {
            slider.setPointerCapture && slider.setPointerCapture(e.pointerId);
        } catch {}
        wrapper.classList.add("no-select"), disableSliderTransition(), e.preventDefault();
    });

    function onWindowPointerMoveForSlider(e) {
        if (!isDragging) return;
        const dx = e.clientX - startX,
            newLeft = Math.max(0, Math.min(maxScroll, startScroll - dx * 1));
        nativeScroller && (nativeScroller.scrollLeft = newLeft), syncFromNative();
    }

    function onWindowPointerUpForSlider(t) {
        if (isDragging) {
            isDragging = !1;
            try {
                slider.releasePointerCapture && slider.releasePointerCapture(t.pointerId);
            } catch {}
            wrapper.classList.remove("no-select"), restoreSliderTransition();
        }
    }
    window.addEventListener("pointermove", onWindowPointerMoveForSlider),
        window.addEventListener("pointerup", onWindowPointerUpForSlider),

    /* Wheel handler on wrapper
     - Sends wheel deltas to nativeScroller for horizontal movement and prevents default vertical scroll.
  */
        wrapper.addEventListener(
            "wheel",
            function (e) {
                const d = e.deltaX ? e.deltaX : e.deltaY;
                if (d > 0 && (nativeScroller && nativeScroller.scrollLeft >= maxScroll)) return;
                if (d < 0 && (nativeScroller && nativeScroller.scrollLeft <= 0)) return;
                nativeScroller && (nativeScroller.scrollLeft = Math.max(0, Math.min(maxScroll, (nativeScroller.scrollLeft || 0) + d))), syncFromNative(), e.preventDefault();
            },
            { passive: !1 }
        );

        wrapper.hasAttribute("tabindex") || (wrapper.tabIndex = 0);
    function onKeydown(e) {
        const key = e.key,
            step = Math.max(Math.round(viewPortWidth / 3), 100);
        key === "ArrowLeft"
            ? (nativeScroller && (nativeScroller.scrollLeft = Math.max(0, (nativeScroller.scrollLeft || 0) - step)), syncFromNative(), e.preventDefault())
            : key === "ArrowRight"
              ? (nativeScroller && (nativeScroller.scrollLeft = Math.min(maxScroll, (nativeScroller.scrollLeft || 0) + step)), syncFromNative(), e.preventDefault())
              : key === "Home"
                ? (nativeScroller && (nativeScroller.scrollLeft = 0), syncFromNative(), e.preventDefault())
                : key === "End"
                  ? (nativeScroller && (nativeScroller.scrollLeft = maxScroll), syncFromNative(), e.preventDefault())
                  : key === "PageUp"
                    ? (nativeScroller && (nativeScroller.scrollLeft = Math.max(0, (nativeScroller.scrollLeft || 0) - step * 2)), syncFromNative(), e.preventDefault())
                    : key === "PageDown" &&
                      (nativeScroller && (nativeScroller.scrollLeft = Math.min(maxScroll, (nativeScroller.scrollLeft || 0) + step * 2)), syncFromNative(), e.preventDefault());
    }
    wrapper.addEventListener("keydown", onKeydown),

    // Sync on native scroll events
        nativeScroller &&
            nativeScroller.addEventListener("scroll", function () {
                syncFromNative(), scheduleUpdate();
            });

    // Resize observer: keep sizes updated when slider or wrapper resize
    const ro = window.ResizeObserver ? new ResizeObserver(scheduleUpdate) : null;
    if (ro)
        try {
            ro.observe(slider), ro.observe(wrapper);
        } catch {}
    else window.addEventListener("resize", scheduleUpdate);

    // MutationObserver: watch DOM changes inside slider (useful for filters that hide/show items)
    let mo = null;
    try {
        (mo = new MutationObserver(function () {
            scheduleUpdate();
        })),
            mo.observe(slider, { childList: !0, subtree: !0, attributes: !0, attributeFilter: ["style", "class", "hidden"] });
    } catch {
        mo = null;
    }

    // Images: schedule updates when images load and change layout
    slider.querySelectorAll("img").forEach(function (img) {
        img.complete || img.addEventListener("load", scheduleUpdate);
    });

    // Initial measurement
    scheduleUpdate();

    // Instance API
    const instance = {
        update: updateSizes,
        scheduleUpdate: scheduleUpdate,
        destroy: function () {
            // Remove global handlers and observers
            if (
                (window.removeEventListener("pointermove", onWindowPointerMoveForThumb),
                window.removeEventListener("pointerup", onWindowPointerUpForThumb),
                window.removeEventListener("pointermove", onWindowPointerMoveForSlider),
                window.removeEventListener("pointerup", onWindowPointerUpForSlider),
                replicaThumb)
            )
                try {
                    replicaThumb.removeEventListener("pointerdown", onReplicaThumbPointerDown);
                } catch {}
            wrapper.removeEventListener("keydown", onKeydown);
            try {
                ro && ro.disconnect(), mo && mo.disconnect();
            } catch {}
        },
    };

    // store instance on wrapper for idempotent access
    try {
        wrapper._horizontalScrollerInstance = instance;
    } catch {}
    return instance;
}
try {
    typeof Element < "u" &&
        !Element.prototype.HorizontalScroller &&
        Object.defineProperty(Element.prototype, "HorizontalScroller", {
            configurable: !0,
            value: function (options) {
                return this._horizontalScrollerInstance && !(options && options.force)
                    ? this._horizontalScrollerInstance
                    : createInstance(this, options || {});
            },
        });
} catch {}
//# sourceMappingURL=horizontal-scroller-tZQ06VkI.js.map
